import type { BibleBook } from "./types";

/** Romans to Philemon — the thirteen letters carrying Paul's name. */
export const LETTERS: BibleBook[] = [
  {
    slug: "romans",
    name: "Romans",
    testament: "new",
    genre: "letter",
    order: 45,
    chapters: 16,
    author: "Paul, dictated to Tertius",
    written: "Around 57 AD, from Corinth",
    audience: "A church in Rome he had never visited",
    argument:
      "Paul's most systematic letter, written to introduce himself to a church he hopes will fund a trip to Spain, and the most influential document in Christian history after the gospels.",
    summary: [
      "Romans is the only major Pauline letter written to a church he had not founded or visited, which is why it explains itself so carefully. He is introducing his message before arriving, and hoping for support for a mission further west.",
      "The first eight chapters build the argument: everyone is in the same position, righteousness comes through faith rather than performance, and the consequence is a life no longer run by fear. Chapters 9 to 11 then wrestle with what that means for Israel, which is the section most readers skip and Paul evidently could not.",
      "From chapter 12 it turns practical — bodies as living sacrifices, minds renewed rather than conformed, honest instruction about living with people you disagree with.",
    ],
    startHere:
      "Chapter 8, which is the summit of the argument and readable on its own. Then chapter 12, where it becomes practical.",
    themes: ["righteousness", "faith", "Israel", "transformed living"],
    keyChapters: [
      {
        chapter: 5,
        why: "Peace with God, and the argument from Adam that sets up everything after it.",
      },
      {
        chapter: 8,
        why: "The summit — no condemnation, the Spirit praying in wordless groans, and nothing able to separate.",
      },
      {
        chapter: 12,
        why: "Where the letter turns practical: bodies as living sacrifices and minds renewed rather than conformed.",
      },
    ],
    known: {
      text: "We know that all things work together for good for those who love God, for those who are called according to his purpose.",
      ref: "Romans 8:28",
    },
    misread:
      "That 8:28 promises that everything will turn out well. The sentence is about God working within all things toward a stated purpose, and the verses immediately around it are about suffering, groaning and the Spirit praying in words that cannot be uttered.",
    faq: [
      {
        q: "What is the book of Romans about?",
        a: "Paul's fullest statement of his message: the human situation, righteousness through faith, life in the Spirit, the place of Israel, and then practical instruction for a mixed community.",
      },
      {
        q: "Why did Paul write Romans?",
        a: "To introduce himself and his message to a church he had never visited, partly in hope of their support for a mission to Spain.",
      },
    ],
    passages: ["romans-12-2", "romans-8-28"],
  },
  {
    slug: "1-corinthians",
    name: "1 Corinthians",
    testament: "new",
    genre: "letter",
    order: 46,
    chapters: 16,
    author: "Paul",
    written: "Around 54 AD, from Ephesus",
    audience: "A gifted, divided, badly behaved church in a port city",
    argument:
      "A letter answering a list of problems in a church that was spiritually impressive and socially disgraceful, and the reason the love chapter exists.",
    summary: [
      "Corinth was a wealthy port and its church had absorbed the city's habits. The letter answers a series of reported problems and written questions: factions built around favourite teachers, a lawsuit between members, a sexual scandal the church was proud of tolerating, disorder at communal meals where the rich ate first and the poor went hungry.",
      "The chapters on spiritual gifts sit in that context, which is what most people miss about chapter 13. The love passage read at weddings is not a wedding text; it is the middle of an argument with a congregation whose gifts had become a status competition.",
      "Chapter 15 is the earliest written account of the resurrection appearances, predating the gospels by some years.",
    ],
    startHere:
      "Chapter 13, but read chapters 12 and 14 around it. The famous passage is an intervention in a dispute and reads completely differently in place.",
    themes: ["division", "the cross", "gifts", "resurrection"],
    keyChapters: [
      {
        chapter: 1,
        why: "The message of the cross as foolishness, aimed at a church impressed by cleverness.",
      },
      {
        chapter: 13,
        why: "The love chapter, sitting between two chapters about spiritual gifts being used competitively.",
      },
      {
        chapter: 15,
        why: "The earliest written account of the resurrection appearances, predating the gospels.",
      },
    ],
    known: {
      text: "But now faith, hope, and love remain—these three. The greatest of these is love.",
      ref: "1 Corinthians 13:13",
    },
    misread:
      "That chapter 13 is about romantic love. It sits between two chapters about spiritual gifts and is addressed to people using their gifts to rank each other.",
    faq: [
      {
        q: "What problems does 1 Corinthians address?",
        a: "Factions, a lawsuit, sexual immorality, food offered to idols, disorder at communal meals, competition over spiritual gifts, and confusion about the resurrection.",
      },
      {
        q: "Is 1 Corinthians 13 a wedding reading?",
        a: "It is commonly used as one, but its setting is a dispute about spiritual gifts in a divided church. Read in place it is considerably more pointed than it sounds at a wedding.",
      },
    ],
    passages: ["1-corinthians-13"],
    topics: ["bible-verses-about-temptation"],
  },
  {
    slug: "2-corinthians",
    name: "2 Corinthians",
    testament: "new",
    genre: "letter",
    order: 47,
    chapters: 13,
    author: "Paul",
    written: "Around 55 to 56 AD",
    audience: "The same church, after a painful falling out",
    argument:
      "The most personal thing Paul wrote — a defence of his own ministry against rivals, in which he boasts only about his failures.",
    summary: [
      "Something went badly wrong between 1 and 2 Corinthians. There was a painful visit, a severe letter that has not survived, and rival teachers who impressed the Corinthians with credentials and eloquence Paul did not have.",
      "So he defends himself, and the way he does it is the letter's whole argument. Asked to list his qualifications, he lists beatings, shipwrecks, imprisonments, sleepless nights and an escape from a city in a basket. The credential is weakness.",
      "That is where the thorn in the flesh passage sits — an unnamed affliction he asked three times to have removed, and the answer he received instead, which is one of the most quoted sentences in the New Testament.",
    ],
    startHere:
      "Chapter 12, for the thorn and the answer. Chapter 4 if you want the letter's argument in its most compressed form.",
    themes: ["weakness", "comfort", "ministry", "generosity"],
    keyChapters: [
      {
        chapter: 4,
        why: "Treasure in clay jars — the letter's argument in its most compressed form.",
      },
      {
        chapter: 8,
        why: "The collection for Jerusalem, and the most sustained New Testament passage about money.",
      },
      {
        chapter: 12,
        why: "The thorn in the flesh, and the answer he received after asking three times.",
      },
    ],
    known: {
      text: "My grace is sufficient for you, for my power is made perfect in weakness.",
      ref: "2 Corinthians 12:9",
    },
    misread:
      "That the thorn passage recommends weakness. Paul asked three times for it to be removed and did not get what he wanted; he is reporting what he was given instead, not commending the condition.",
    faq: [
      {
        q: "What was Paul's thorn in the flesh?",
        a: "He does not say. Proposals include an illness, an eye condition, a persistent opponent and a recurring temptation. The passage's point is the answer he received rather than the affliction's identity.",
      },
      {
        q: "Why is 2 Corinthians so personal?",
        a: "Because Paul is defending his ministry against rivals with better credentials, to a church that had partly turned against him. The defence takes the form of listing his sufferings rather than his achievements.",
      },
    ],
    topics: ["bible-verses-about-strength"],
  },
  {
    slug: "galatians",
    name: "Galatians",
    testament: "new",
    genre: "letter",
    order: 48,
    chapters: 6,
    author: "Paul",
    written: "Around 48 to 55 AD, depending on the destination",
    audience: "Churches being told they must adopt Jewish law to belong",
    argument:
      "The angriest letter in the New Testament — no thanksgiving, no pleasantries, straight into an accusation — because Paul thought the gospel itself was at stake.",
    summary: [
      "Every other Pauline letter opens with thanks for its readers. Galatians opens by expressing astonishment that they are deserting the gospel, and it does not soften from there.",
      "The issue is whether Gentile Christians must be circumcised and keep the law to be full members. Paul's answer is that requiring it makes the cross pointless, and he argues the case from Abraham, from the law's own function, and from his own confrontation with Peter in Antioch.",
      "The last two chapters turn to what freedom actually looks like, and contain the list of the fruit of the Spirit — which is Paul's answer to the obvious objection that removing the law removes the brakes.",
    ],
    startHere:
      "Chapter 5, which contains both the argument about freedom and the fruit of the Spirit list.",
    themes: ["freedom", "law and faith", "inclusion", "the Spirit"],
    keyChapters: [
      {
        chapter: 2,
        why: "The confrontation with Peter at Antioch, recorded by the man who started it.",
      },
      {
        chapter: 3,
        why: "The argument from Abraham, and the line about neither Jew nor Greek, slave nor free, male nor female.",
      },
      {
        chapter: 5,
        why: "Freedom, and the fruit of the Spirit listed as one fruit rather than nine.",
      },
    ],
    known: {
      text: "I have been crucified with Christ, and it is no longer I who live, but Christ lives in me.",
      ref: "Galatians 2:20",
    },
    misread:
      "That freedom in Galatians means no obligations. Paul spends the last two chapters insisting the opposite — freedom is for serving one another, and the works of the flesh he lists are mostly social.",
    faq: [
      {
        q: "What is the main argument of Galatians?",
        a: "That Gentile Christians do not have to adopt the Jewish law to belong, and that requiring it empties the cross of meaning. It is the New Testament's sharpest statement of that case.",
      },
      {
        q: "What is the fruit of the Spirit?",
        a: "Love, joy, peace, patience, kindness, goodness, faith, gentleness and self-control — Galatians 5:22–23, listed as a single fruit rather than nine separate ones.",
      },
    ],
    topics: ["bible-verses-about-self-control"],
  },
  {
    slug: "ephesians",
    name: "Ephesians",
    testament: "new",
    genre: "letter",
    order: 49,
    chapters: 6,
    author: "Paul, though authorship is debated",
    written: "Around 60 to 62 AD if by Paul; possibly later",
    audience: "Probably a circular letter to several churches",
    argument:
      "Three chapters of what has happened and three of what to do about it, joined by the word therefore — the cleanest structure of any New Testament letter.",
    summary: [
      "Ephesians is unusually general for a Pauline letter — no local problems, no names, no crisis — which is why many take it as a circular sent to several churches in the region.",
      "Its structure is the thing to know. Chapters 1 to 3 are entirely about what God has done: chosen, adopted, raised, reconciled, made one people out of two. Chapters 4 to 6 are entirely about conduct, and they begin with therefore.",
      "The last section, the armour of God, is the most quoted part and sits at the end of an argument about a community learning to speak truthfully to each other.",
    ],
    startHere:
      "Chapter 2, which contains the letter's central claim about grace and its argument about two groups becoming one.",
    themes: ["grace", "one people", "unity", "standing firm"],
    keyChapters: [
      {
        chapter: 2,
        why: "By grace you have been saved, and the wall of hostility broken down between two groups.",
      },
      {
        chapter: 4,
        why: "The therefore — where the letter turns from what God has done to how to live.",
      },
      {
        chapter: 6,
        why: "The armour of God, all of it defensive except one piece, followed by an instruction to pray.",
      },
    ],
    known: {
      text: "for by grace you have been saved through faith, and that not of yourselves; it is the gift of God,",
      ref: "Ephesians 2:8",
    },
    misread:
      "That the armour of God is about personal spiritual warfare in isolation. The passage closes a letter about a community, the pieces are all defensive except one, and the instruction attached is to pray for each other.",
    faq: [
      {
        q: "What is the structure of Ephesians?",
        a: "Three chapters on what God has done, then three on how to live, joined by therefore at the start of chapter 4. It is the clearest example of that pattern in Paul's letters.",
      },
      {
        q: "What is the armour of God?",
        a: "Ephesians 6:10–18: truth, righteousness, readiness, faith, salvation and the word, described as equipment for standing rather than attacking, and followed immediately by an instruction to pray.",
      },
    ],
  },
  {
    slug: "philippians",
    name: "Philippians",
    testament: "new",
    genre: "letter",
    order: 50,
    chapters: 4,
    author: "Paul",
    written: "Around 60 to 62 AD, from prison",
    audience: "A church that had supported him financially for years",
    argument:
      "A thank-you letter written from custody that mentions joy fourteen times, and contains an early hymn about someone giving up status voluntarily.",
    summary: [
      "Philippi was Paul's first European congregation and evidently his favourite. The letter is warm, personal and written from prison, which is what makes its repeated language of joy notable rather than sentimental.",
      "Chapter 2 contains what most scholars think is a pre-existing hymn, quoted by Paul: a description of someone who did not grasp at equality with God but emptied himself, took a servant's form and was obedient to death. It is the earliest high statement about Christ in the New Testament.",
      "Chapter 4 supplies two of the most quoted verses in the Bible, both of which are routinely detached from what surrounds them.",
    ],
    startHere:
      "Chapter 2, for the hymn, then chapter 4 for the passage about anxiety and contentment.",
    themes: ["joy", "humility", "contentment", "partnership"],
    keyChapters: [
      {
        chapter: 1,
        why: "To live is Christ and to die is gain, written by a man genuinely uncertain which was coming.",
      },
      {
        chapter: 2,
        why: "The hymn about emptying and obedience, probably older than the letter that quotes it.",
      },
      {
        chapter: 4,
        why: "Anxiety, contentment, and the two most quoted verses in the letter.",
      },
    ],
    known: {
      text: "I can do all things through Christ who strengthens me.",
      ref: "Philippians 4:13",
    },
    misread:
      "That 4:13 is about achievement. The preceding verse lists the all things — being filled and being hungry, abounding and being in need — so it is a claim about handling both ends of a circumstance, written from custody.",
    faq: [
      {
        q: "Why is Philippians called the letter of joy?",
        a: "Because joy and rejoicing appear about fourteen times in four chapters, written by a man in custody with an uncertain outcome — which is what gives the word its weight here.",
      },
      {
        q: "What does Philippians 4:13 actually mean?",
        a: "Read with verse 12, it is about being able to handle both plenty and need rather than about accomplishing whatever you attempt. Paul is describing contentment learned in extremes.",
      },
    ],
    passages: ["philippians-4-13", "philippians-4-6-7"],
    topics: ["bible-verses-about-peace"],
  },
  {
    slug: "colossians",
    name: "Colossians",
    testament: "new",
    genre: "letter",
    order: 51,
    chapters: 4,
    author: "Paul, though authorship is debated",
    written: "Around 60 to 62 AD if by Paul",
    audience: "A church Paul had never visited, facing an unnamed teaching",
    argument:
      "A short letter against a rival teaching we can only reconstruct from the answers, arguing that nothing needs adding to Christ.",
    summary: [
      "Colossae was a small town Paul had not been to; the church was founded by Epaphras. Some teaching had arrived that involved dietary rules, festivals, angelic worship and self-denial, and the letter answers it without ever describing it fully.",
      "Paul's response is not to argue point by point but to make a claim about scope: everything holds together in Christ, so nothing supplementary is required. The hymn in chapter 1 is where that case is made.",
      "The practical section that follows has the household instructions and the line about working heartily, which was addressed to slaves — a fact worth keeping in view given how the verse is used now.",
    ],
    startHere:
      "Chapter 1, verses 15 to 20, which is the hymn and the letter's whole argument in six verses.",
    themes: ["the supremacy of Christ", "sufficiency", "new self", "household"],
    keyChapters: [
      {
        chapter: 1,
        why: "The hymn — everything holds together in him — which is the letter's entire argument.",
      },
      {
        chapter: 2,
        why: "The answer to the rival teaching: do not let anyone disqualify you with rules and visions.",
      },
      {
        chapter: 3,
        why: "Setting the mind above, and the practical instructions that follow immediately.",
      },
    ],
    known: {
      text: "And whatever you do, work heartily, as for the Lord and not for men,",
      ref: "Colossians 3:23",
    },
    misread:
      "That 3:23 is a productivity verse for employees. It is addressed to household slaves, in a passage that then turns to their masters, and using it to demand more from workers reverses its direction.",
    faq: [
      {
        q: "What false teaching does Colossians address?",
        a: "Something involving dietary rules, festivals, angelic worship and harsh treatment of the body. The letter never names it directly, so it can only be reconstructed from the answers.",
      },
      {
        q: "Who was Colossians written to?",
        a: "A small church in Colossae that Paul had not founded or visited, established by Epaphras, who evidently brought the report that prompted the letter.",
      },
    ],
  },
  {
    slug: "1-thessalonians",
    name: "1 Thessalonians",
    testament: "new",
    genre: "letter",
    order: 52,
    chapters: 5,
    author: "Paul",
    written: "Around 50 AD — probably the earliest book in the New Testament",
    audience: "A very young church he had to leave in a hurry",
    argument:
      "Probably the oldest surviving Christian document, written to a congregation a few months old that was worried its dead had missed out.",
    summary: [
      "Paul was run out of Thessalonica after a short stay, and this letter follows soon after — most scholars date it around 50 AD, which makes it likely the earliest text in the New Testament, written before any of the gospels.",
      "It is mostly encouragement and relief: Timothy has brought back a good report. The one problem it answers is grief. Members of the congregation had died, and the survivors feared they would miss the return of Christ.",
      "The answer given is not a timetable but a reassurance, and it closes with a run of short instructions including the three-part line about rejoicing, praying and giving thanks.",
    ],
    startHere:
      "Chapter 4, from verse 13, which is the pastoral heart of the letter.",
    themes: ["encouragement", "hope", "work", "the return"],
    keyChapters: [
      {
        chapter: 4,
        why: "The answer about those who have died, given to a congregation that had just buried people.",
      },
      {
        chapter: 5,
        why: "Rejoice always, pray continually, give thanks in everything — and a run of short instructions.",
      },
    ],
    known: {
      text: "Always rejoice. Pray without ceasing. In everything give thanks, for this is the will of God in Christ Jesus toward you.",
      ref: "1 Thessalonians 5:16–18",
    },
    misread:
      "That give thanks in everything means give thanks for everything. The preposition is in, and it is written to people who had just buried members of their congregation.",
    faq: [
      {
        q: "Is 1 Thessalonians the oldest book in the New Testament?",
        a: "Most scholars think so, dating it around 50 AD — roughly twenty years before the earliest gospel.",
      },
      {
        q: "What question does 1 Thessalonians answer?",
        a: "Whether Christians who had already died would miss out on Christ's return. Paul's answer is reassurance about the dead rather than a timetable for the living.",
      },
    ],
  },
  {
    slug: "2-thessalonians",
    name: "2 Thessalonians",
    testament: "new",
    genre: "letter",
    order: 53,
    chapters: 3,
    author: "Paul, though authorship is debated",
    written: "Shortly after the first letter, around 50 to 51 AD",
    audience: "The same church, now with a rumour problem",
    argument:
      "A follow-up letter written because somebody had told the church the day of the Lord had already come, and some of them had stopped working.",
    summary: [
      "The problem in the second letter is the mirror image of the first. There, people worried the dead had missed the return; here, someone has claimed it has already happened, possibly in a forged letter carrying Paul's name.",
      "So the letter slows things down: certain things have to happen first, and the church should not be shaken by every report. It is the New Testament's most direct instruction not to be alarmed by end-times claims.",
      "The practical consequence being addressed is unusual and specific. Some members had stopped working, presumably because the end was imminent, and were living off others. Paul's response is blunt: anyone unwilling to work should not eat.",
    ],
    startHere:
      "Chapter 3, which is short, practical, and the part with lasting bite.",
    themes: ["patience", "false alarms", "work", "perseverance"],
    keyChapters: [
      {
        chapter: 2,
        why: "Do not be shaken by any report that the day has already come.",
      },
      {
        chapter: 3,
        why: "The instruction about those who had stopped working because they thought the end had arrived.",
      },
    ],
    known: {
      text: "But the Lord is faithful, who will establish you and guard you from the evil one.",
      ref: "2 Thessalonians 3:3",
    },
    misread:
      "That the working instruction is about the poor. It addresses people who had stopped working on purpose because they believed the world was ending, and were being supported by the congregation.",
    faq: [
      {
        q: "Why was 2 Thessalonians written?",
        a: "Because a claim was circulating — possibly in a forged letter — that the day of the Lord had already arrived, and some members had stopped working as a result.",
      },
      {
        q: "What does if anyone will not work, let him not eat mean?",
        a: "It addresses people who had deliberately stopped working because they thought the end had come and were living off the congregation. It is not a comment about people unable to find work.",
      },
    ],
  },
  {
    slug: "1-timothy",
    name: "1 Timothy",
    testament: "new",
    genre: "letter",
    order: 54,
    chapters: 6,
    author: "Paul, though authorship is widely debated",
    written: "Around 62 to 64 AD if by Paul; possibly later",
    audience: "A young colleague left in charge in Ephesus",
    argument:
      "The first of the pastoral letters — practical instructions to a young leader about who should lead, how to handle money, and what to do with disruptive teaching.",
    summary: [
      "1 Timothy is written to an individual rather than a congregation, and it reads like a job briefing. Timothy has been left in Ephesus to deal with teachers producing speculation and controversy rather than anything useful.",
      "Most of the letter is organisational: qualifications for overseers and deacons, the care of widows, the treatment of elders, instructions about wealth. It is the New Testament's most administrative book.",
      "It also contains one of its most contested passages, on women teaching, which has been argued over intensely for decades and which any honest summary should flag rather than resolve in passing.",
    ],
    startHere:
      "Chapter 6, on money and contentment, which is the most immediately useful section.",
    themes: ["church order", "leadership", "money", "sound teaching"],
    keyChapters: [
      {
        chapter: 3,
        why: "Qualifications for overseers and deacons, almost all of them about character rather than ability.",
      },
      {
        chapter: 6,
        why: "Contentment and the love of money, which is the letter's most quoted and most misquoted passage.",
      },
    ],
    known: {
      text: "Let no man despise your youth; but be an example to those who believe, in word, in your way of life, in love, in spirit, in faith, and in purity.",
      ref: "1 Timothy 4:12",
    },
    misread:
      "That money is the root of all evil. The verse says the love of money is a root of all kinds of evil, and the surrounding passage is about contentment rather than about wealth as such.",
    faq: [
      {
        q: "What are the pastoral epistles?",
        a: "1 Timothy, 2 Timothy and Titus — letters addressed to individuals rather than congregations, concerned with church order and leadership.",
      },
      {
        q: "Did Paul write 1 Timothy?",
        a: "Tradition says yes. A significant body of modern scholarship questions it on grounds of vocabulary, style and the church structure described, and the question remains genuinely open.",
      },
    ],
  },
  {
    slug: "2-timothy",
    name: "2 Timothy",
    testament: "new",
    genre: "letter",
    order: 55,
    chapters: 4,
    author: "Paul, though authorship is debated",
    written: "Around 64 to 67 AD if by Paul",
    audience: "The same colleague, from what reads like a final imprisonment",
    argument:
      "The most personal and most final of the letters — a man in a cold cell asking for his cloak and his books, and telling somebody younger to keep going.",
    summary: [
      "2 Timothy reads as a last letter. Paul is imprisoned, most of his companions have gone, and he asks Timothy to come before winter and to bring the cloak and the scrolls he left behind. Those details are what give the letter its texture.",
      "Its instruction is to continue: guard what was entrusted, endure hardship, preach whether or not it is convenient. The tone is handover rather than argument.",
      "It also contains the sentence most often quoted about Scripture itself, in a passage about what a young leader should rely on when everything else is unstable.",
    ],
    startHere:
      "Chapter 4, which is the closing section and the most human passage Paul wrote.",
    themes: ["endurance", "handover", "Scripture", "loneliness"],
    keyChapters: [
      {
        chapter: 2,
        why: "The images of a soldier, an athlete and a farmer, all of them about staying at it.",
      },
      {
        chapter: 3,
        why: "All Scripture is God-breathed, in a passage about what to rely on when everything is unstable.",
      },
      {
        chapter: 4,
        why: "The last chapter — bring the cloak and the scrolls, and come before winter.",
      },
    ],
    known: {
      text: "Every Scripture is God-breathed and profitable for teaching, for reproof, for correction, and for instruction in righteousness,",
      ref: "2 Timothy 3:16",
    },
    misread:
      "That the Scripture verse is making a claim about the New Testament. When it was written the New Testament did not exist as a collection; the immediate reference is the Jewish Scriptures Timothy had known since childhood.",
    faq: [
      {
        q: "What is 2 Timothy about?",
        a: "A handover. Paul, imprisoned and largely abandoned, urges Timothy to hold on to what he has been taught, endure hardship and keep teaching — with unusually personal requests at the end.",
      },
      {
        q: "What does God-breathed mean?",
        a: "The Greek word appears only here in the New Testament and literally means breathed out by God. What it implies in detail has been debated for centuries; the passage's own emphasis is on usefulness for teaching and correction.",
      },
    ],
    passages: ["2-timothy-1-7"],
  },
  {
    slug: "titus",
    name: "Titus",
    testament: "new",
    genre: "letter",
    order: 56,
    chapters: 3,
    author: "Paul, though authorship is debated",
    written: "Around 63 to 65 AD if by Paul",
    audience: "A colleague organising churches on Crete",
    argument:
      "Three chapters on appointing leaders and behaving well in a place with a bad reputation, containing the sentence that makes grace and discipline the same movement.",
    summary: [
      "Titus has been left on Crete to appoint elders town by town and to deal with disruptive teachers. The letter is short and organisational, much like 1 Timothy but more compressed.",
      "It is unusually blunt about its setting, quoting a Cretan poet's insult about Cretans and agreeing with it, which has made the passage awkward reading ever since.",
      "Its lasting contribution is chapter 2, where grace is described as the thing that instructs — a sentence that forecloses the argument that grace and effort are alternatives.",
    ],
    startHere:
      "Chapter 2, from verse 11, which is the theological centre of the letter.",
    themes: ["leadership", "grace that trains", "good works", "reputation"],
    keyChapters: [
      {
        chapter: 1,
        why: "Appointing elders town by town, and the bluntest description of a local population anywhere in the New Testament.",
      },
      {
        chapter: 2,
        why: "Grace as the thing that instructs, which forecloses the argument that grace and effort are alternatives.",
      },
    ],
    known: {
      text: "not by works of righteousness which we did ourselves, but according to his mercy, he saved us through the washing of regeneration and renewing by the Holy Spirit,",
      ref: "Titus 3:5",
    },
    misread:
      "That it is only an administrative letter. Its two doctrinal paragraphs are among the most compressed statements of grace in the New Testament, and both are attached directly to how people behave.",
    faq: [
      {
        q: "What is the letter to Titus about?",
        a: "Appointing elders across the churches of Crete, dealing with disruptive teaching, and how a community with a poor local reputation should behave.",
      },
      {
        q: "What does Titus say about grace?",
        a: "Chapter 2:11–12 makes grace the subject of the verb: grace appeared, and it instructs us to live soberly and righteously now. It is the clearest statement that grace and discipline are not alternatives.",
      },
    ],
  },
  {
    slug: "philemon",
    name: "Philemon",
    testament: "new",
    genre: "letter",
    order: 57,
    chapters: 1,
    author: "Paul",
    written: "Around 60 to 62 AD, from prison",
    audience: "One man, and the church that met in his house",
    argument:
      "A single-chapter personal letter about a runaway slave, which never demands anything and makes refusal almost impossible.",
    summary: [
      "Philemon is twenty-five verses long and concerns one situation: Onesimus, a slave, has left Philemon's household and has since become a Christian while with Paul. Paul is sending him back with this letter.",
      "The letter's method is its argument. Paul says explicitly that he could command and will not, offers to pay any debt himself, mentions that Philemon owes him his own life, and asks him to receive Onesimus as a brother rather than a slave.",
      "It does not abolish slavery and it is not a treatise. It applies a relationship to a specific case, in a way that would be very difficult to live with and keep the institution intact.",
    ],
    startHere:
      "The whole letter. It is twenty-five verses and takes four minutes.",
    themes: ["reconciliation", "slavery", "persuasion", "brotherhood"],
    keyChapters: [
      {
        chapter: 1,
        why: "The whole letter — twenty-five verses that never command and make refusal almost impossible.",
      },
    ],
    known: {
      text: "no longer as a slave, but more than a slave, a beloved brother—especially to me, but how much rather to you, both in the flesh and in the Lord.",
      ref: "Philemon 1:16",
    },
    misread:
      "That Paul endorses slavery by returning Onesimus. The letter asks for him to be received as a brother and hints at more, while carefully leaving the decision with Philemon rather than commanding it.",
    faq: [
      {
        q: "What is the book of Philemon about?",
        a: "Paul writing to a slave owner about Onesimus, a slave who had left and become a Christian, asking that he be received back as a brother rather than as property.",
      },
      {
        q: "Does Philemon support slavery?",
        a: "It does not attack the institution directly, and it asks for a relationship — brother rather than slave — that is very hard to combine with owning someone. Christians have argued about that gap for centuries.",
      },
    ],
  },
];
